import styled from "styled-components";
import { RAButtonPrimary, RAImage, RATypography } from "@/components/customs";
import { Row, Col, Modal, Divider, Grid, Popover } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { CardDetails } from "./CardDetails";
import { convertKnotToMph, formatCurrency } from "@/utils";
import { SvgAirplaneSeat } from "@/assets/icons";
import { useEffect, useState } from "react";
import { getEmptyLegsById } from "@/stores/apis/apiCharter";
import { useSelector } from "react-redux";
import { selectAuthState } from "@/stores/slices/authSlice";
const { useBreakpoint } = Grid;

export function ModalEmptyLegDetails({ visible = false, onClose, booking = {}, bookingId = null, onSendInquiry }) {
	const screens = useBreakpoint();
	const { user } = useSelector(selectAuthState);
	const [details, setDetails] = useState({});

	const onFetchBookingEmptyLegDetails = async (id) => {
		const response = await getEmptyLegsById(id);
		if (response && response.success) {
			setDetails(response?.data || booking);
		}
	};

	useEffect(() => {
		if (bookingId) {
			onFetchBookingEmptyLegDetails(bookingId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookingId]);

	const _renderAircraftInfo = () => {
		const { images = [] } = details?.aircraft || {};
		return (
			<WrapperAircraft>
				<RATypography variant="sub-heading4" color="#121216">
					Aircraft
				</RATypography>
				<WrapperAircraftInfo>
					<Row gutter={[20, 16]}>
						<Col md={8} xs={10}>
							<RAImage
								url={images && images[0] ? images[0].url : "/images/example/aircraft_1.png"}
								width="100%"
								height={screens.xs ? "80px" : "100px"}
								borderRadius="6px"
							/>
						</Col>
						<Col md={16} xs={14}>
							<RATypography variant="sub-heading3" color="#121216">
								{details?.aircraft?.name ?? "_"}
							</RATypography>
							<RATypography variant="body3" color="#65686F" maxLines={4}>
								{details?.aircraft?.description ?? ""}
							</RATypography>
						</Col>
					</Row>
					<Row
						gutter={[
							{
								xs: 0,
								sm: 0,
								md: 20,
								lg: 20,
								xl: 20,
								xxl: 20,
							},

							{ xs: 8, sm: 8, md: 16, lg: 16, xl: 16, xxl: 16 },
						]}
						style={{ marginTop: 10 }}
					>
						<Col xxl={7} xl={7} lg={7} md={7} sm={24} xs={24}>
							<Row align="middle" gutter={[20, 8]}>
								<Col xxl={24} xl={24} lg={24} md={24} sm={12} xs={12}>
									<RATypography variant="body4" color="#65686F">
										Aircraft Category
									</RATypography>
								</Col>
								<Col>
									<RATypography variant="sub-heading4" color="#121216">
										{details?.aircraft?.aircraft_category?.name ?? "_"}
									</RATypography>
								</Col>
							</Row>
						</Col>
						<Col xxl={7} xl={7} lg={7} md={7} sm={24} xs={24}>
							<Row align="middle" gutter={[20, 8]}>
								<Col xxl={24} xl={24} lg={24} md={24} sm={12} xs={12}>
									<RATypography variant="body4" color="#65686F">
										Last Refurbished
									</RATypography>
								</Col>
								<Col>
									<RATypography variant="sub-heading4" color="#121216">
										{details?.aircraft?.last_refurbished ?? "_"}
									</RATypography>
								</Col>
							</Row>
						</Col>
						<Col xxl={7} xl={7} lg={7} md={7} sm={24} xs={24}>
							<Row align="middle" gutter={[20, 8]}>
								<Col xxl={24} xl={24} lg={24} md={24} sm={12} xs={12}>
									<RATypography variant="body4" color="#65686F">
										Max Cruise Speed
									</RATypography>
								</Col>
								<Col>
									<RATypography variant="sub-heading4" color="#121216">
										{`${convertKnotToMph(
											details?.aircraft?.properties?.cruising_speed_in_knots || 0,
										)} MPH`}
									</RATypography>
								</Col>
							</Row>
						</Col>
					</Row>

					<Row align="middle" justify="start" style={{ marginTop: 10 }}>
						<Col>
							<SvgAirplaneSeat />
						</Col>
						<Col>
							<RATypography variant="sub-heading4" color="#65686F" left={12} right={12}>
								{`${details?.aircraft?.max_pax ?? "_"} Pax`}
							</RATypography>
						</Col>
						{details?.aircraft?.amenities?.map((item, index) => (
							<Popover
								key={index}
								placement="bottom"
								title=""
								content={() => {
									return (
										<RATypography variant="body4" color="#595959">
											{item?.name || ""}
										</RATypography>
									);
								}}
							>
								<Col>
									<AmenitiesItem>
										<RAImage url={item?.image?.url} width="20px" height="20px" />
									</AmenitiesItem>
								</Col>
							</Popover>
						))}
					</Row>
				</WrapperAircraftInfo>
			</WrapperAircraft>
		);
	};

	const _renderSendInquiry = () => {
		if (details?.label && details?.label !== "EXPIRED" && details?.label !== "REQUESTED" && user?.activated) {
			return (
				<Row justify={screens?.md ? "end" : "center"}>
					<RAButtonPrimary
						title="SEND INQUIRY"
						onClick={() => onSendInquiry(details)}
						data-testid="emptyLegDetailsModal_sendInquiryBtn"
					/>
				</Row>
			);
		}
	};

	return (
		<StyledModal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => onClose(false)}
			footer={null}
			width="720px"
			wrapClassName="auth-wrapper"
			bodyStyle={{ padding: 0 }}
		>
			<ContainerBookingDetails data-testid="emptyLegDetailsModal_content">
				<WrapperContent>
					<RATypography variant="h3">Empty Leg Details</RATypography>
				</WrapperContent>
				<CardDetails key="flight-request-item-one-way" formValues={details} />
				<Divider />
				{_renderAircraftInfo()}
				<Row gutter={[20, 20]} justify="space-between" align="middle" style={{ marginTop: "30px" }}>
					<Col md={12} xs={24}>
						<Row gutter={[20, 12]} justify="start" align="middle">
							{screens?.md ? (
								<Col>
									<RATypography variant="label3" color="#A8A8A8">
										PRICE
									</RATypography>
								</Col>
							) : (
								<Col span={12}>
									<RATypography variant="label3" color="#A8A8A8">
										PRICE
									</RATypography>
								</Col>
							)}
							<Col>
								<RATypography variant="label1" color="#222222">
									{!details?.price_inc_tax
										? "REQUEST PRICING"
										: formatCurrency(details?.price_inc_tax, "USD", 0)}
								</RATypography>
							</Col>
						</Row>
					</Col>
					<Col md={12} xs={24}>
						{_renderSendInquiry()}
					</Col>
				</Row>
			</ContainerBookingDetails>
		</StyledModal>
	);
}

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background-color: #ffffff;
		border-radius: 24px;
		box-shadow: none;
	}
	.ant-modal-close {
		width: 28px;
		height: 28px;
		top: 22px;
		right: 22px;
		@media (max-width: 576px) {
			top: 22px;
			right: 4px;
		}
	}
	.ant-modal-body {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 80vh;
	}
`;
const ContainerBookingDetails = styled.div`
	width: "100%";
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
const WrapperAircraft = styled.div`
	display: flex;
	flex-direction: column;
`;
const WrapperAircraftInfo = styled.div`
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const AmenitiesItem = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0 12px;
	border-left: 1px solid #e9e8e8;
`;
